import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormResponseStore } from '@/store';
import { useUtmTaxonomyStore } from '@/store/utm-taxonomy-store';
import type { QuestionTypes } from '@/types';
import UtmGeometryPreview from '@/components/utm/shared/UtmGeometryPreview';

type FlightPlanResponseValue = {
  geometry: GeoJSON.Geometry;
  selectionMode?: 'polygon' | 'radius' | 'corridor';
};

interface FlightPlanAnswerProps {
  question: QuestionTypes;
  sectionId: string;
  sectionName: string;
  setValidationErrors: (
    sectionId: string,
    questionId: string,
    error: string | null
  ) => void;
}

export const FlightPlanAnswer: React.FC<FlightPlanAnswerProps> = ({
  question,
  sectionId,
  sectionName,
  setValidationErrors,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { processId, formId } = useParams();
  const { formResponses, setResponse } = useFormResponseStore();
  const clearSelection = useUtmTaxonomyStore((state) => state.clearSelection);

  const existingResponse =
    formResponses?.[sectionId]?.questions?.[question.id]?.response || null;

  const flightPlanValue = useMemo<FlightPlanResponseValue | null>(() => {
    if (!existingResponse || typeof existingResponse !== 'object') {
      return null;
    }

    if (!('geometry' in existingResponse) || !existingResponse.geometry) {
      return null;
    }

    return existingResponse as FlightPlanResponseValue;
  }, [existingResponse]);

  useEffect(() => {
    if (question.required === 'yes' && !flightPlanValue?.geometry) {
      setValidationErrors(sectionId, question.id, `${question.label} ${t('forms.isRequired')}`);
      return;
    }

    setValidationErrors(sectionId, question.id, null);
  }, [question.required, question.label, flightPlanValue, sectionId, question.id, setValidationErrors, t]);

  const handleOpenSelector = () => {
    if (!processId || !formId) {
      return;
    }

    const flightPlanSelection = flightPlanValue?.geometry
      ? {
          geometry: flightPlanValue.geometry,
          selectionMode: flightPlanValue.selectionMode,
        }
      : null;

    if (!flightPlanSelection) {
      clearSelection();
    }

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('flightPlanNavigationInProgress', '1');
    }

    const params = new URLSearchParams({
      sectionName,
      label: question.label || t('questionDesign.flightPlanDefaultLabel'),
      required: question.required || 'no',
    });

    navigate(
      `/answer-application/${processId}/${formId}/flight-plan/${sectionId}/${question.id}?${params.toString()}`,
      {
        state: flightPlanSelection
          ? { flightPlanSelection }
          : undefined,
      }
    );
  };

  const handleClear = () => {
    setResponse(
      sectionId,
      sectionName,
      question.id,
      question.type,
      question.label || t('questionDesign.flightPlanDefaultLabel'),
      null
    );

    if (question.required === 'yes') {
      setValidationErrors(sectionId, question.id, `${question.label} ${t('forms.isRequired')}`);
    } else {
      setValidationErrors(sectionId, question.id, null);
    }
  };

  return (
    <div className="mb-4 space-y-3">
      <label className="main-label">
        {question.label || t('questionDesign.flightPlanDefaultLabel')}{' '}
        {question.required === 'yes' && <span className="text-red-500">*</span>}
      </label>

      {flightPlanValue?.geometry ? (
        <UtmGeometryPreview geometry={flightPlanValue.geometry} height="180px" />
      ) : (
        <div className="rounded border border-dashed border-slate-300 p-4 text-sm text-slate-500 bg-slate-50">
          {t('forms.noFlightPlanSelected')}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleOpenSelector}
          className="px-3 py-2 rounded bg-darkBlue text-white text-sm hover:bg-darkBlue/90"
        >
          {flightPlanValue?.geometry ? t('forms.updateFlightPlan') : t('forms.openFlightPlanMap')}
        </button>

        {flightPlanValue?.geometry && (
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-2 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-100"
          >
            {t('forms.clearChoice')}
          </button>
        )}
      </div>
    </div>
  );
};
